import { Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useMemo, useState } from 'react';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { SuccessFinancialCovenantGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import {
  errorToastConfig,
  getDefaultDashboardDisplayEndDate,
} from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useGetSuccessFinancialCovenantsByCreditFacilityIdQuery } from 'packages/covenants/requests/financialCovenants.gql.generated';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CovenantChartTemplate } from 'packages/portfolio/ui/covenants/CovenantChartTemplate';
import { createCovenantsChartConfig } from 'packages/portfolio/ui/covenants/CovenantsContainer';
import {
  covenantsInitialStartDate,
  financialCovenantValueTypeToPortfolioChartConfigType,
  getAllUniqueCovenantKeys,
} from 'packages/portfolio/ui/shared/portfolioChartUtils';

export const FinancialCovenantsContainer = () => {
  const toast = useToast();
  const { creditFacilityId } = useCreditFacilityContext();

  const [
    allFinancialCovenantsInDefaultRange,
    setAllFinancialCovenantsInDefaultRange,
  ] = useState<SuccessFinancialCovenantGQLType[]>([]);

  const {
    loading: isFinancialCovenantsLoading,
    data: allFinancialCovenantsData,
    refetch: refetchSuccessFinancialCovenants,
  } = useGetSuccessFinancialCovenantsByCreditFacilityIdQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    onCompleted: (data) => {
      if (data.successFinCovs.length) {
        // set the initial set so all configs within the longer initial range are considered
        if (!allFinancialCovenantsInDefaultRange.length) {
          setAllFinancialCovenantsInDefaultRange(data.successFinCovs);
        }
      }
    },
    variables: {
      creditFacilityId,
      mode: 'RESULT',
      startDate: covenantsInitialStartDate,
      endDate: getDefaultDashboardDisplayEndDate(),
    },
  });

  const {
    data: creditAgreementData,
    loading: isCreditAgreementLoading,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    variables: { creditFacilityId },
  });

  const parsedCreditAgreement = useMemo(() => {
    if (creditAgreementData) {
      const creditAgreementString =
        creditAgreementData?.currentCreditAgreementFullArtifact
          ?.creditAgreement;
      const creditAgreementId =
        creditAgreementData?.currentCreditAgreementFullArtifact?.id;

      let parsed = null;
      if (creditAgreementString) {
        try {
          parsed = parseCreditAgreementFullArtifact(creditAgreementString);
        } catch (err) {
          Sentry.captureException(err);

          toast({
            ...errorToastConfig,
            id: 'errorGetCurrentCreditAgreementFullArtifact',
            title: `Error parsing credit agreement artifact for credit agreement ${creditAgreementId}`,
          });
        }
      }

      return parsed;
    }
    return null;
  }, [creditAgreementData, toast]);

  if (
    isFinancialCovenantsLoading ||
    !allFinancialCovenantsData ||
    !creditAgreementData ||
    isCreditAgreementLoading
  ) {
    return (
      <Skeleton
        height="600px"
        data-testid="financial-covenants-insights-loading"
      />
    );
  }

  finleyAssert(
    parsedCreditAgreement,
    `Expect currentCreditAgreementFullArtifact to exist for creditFacilityId ${creditFacilityId}`,
  );

  const { financialCovenants } = parsedCreditAgreement;

  if (!allFinancialCovenantsData.successFinCovs.length) {
    return (
      <InfoAlertWithDescription description="No financial covenant calculations are available in the selected time period." />
    );
  }

  const uniqueFinancialCovenantKeys = getAllUniqueCovenantKeys(
    allFinancialCovenantsInDefaultRange,
  );
  return (
    <>
      {uniqueFinancialCovenantKeys.map((financialCovenantKey) => {
        // it's possible for the covenantKey of a financial covenant calculation to no longer exist in the current CA, if it's been deleted
        // we will still show the calculations and graphs as historical data, but will not be able to parse out the thresholdType (min vs max) later downstream, which is fine
        const creditAgreementFinancialCovenant = financialCovenants.find(
          (financialCovenant) =>
            financialCovenant.covenantKey === financialCovenantKey,
        );

        const finCovValueType =
          creditAgreementFinancialCovenant?.limit.valueType;
        const configType = finCovValueType
          ? financialCovenantValueTypeToPortfolioChartConfigType[
              finCovValueType
            ]
          : 'float';

        return (
          <CovenantChartTemplate
            creditFacilityId={creditFacilityId}
            chartTitle={commonUtils.toTitleCase(financialCovenantKey)}
            covenantKey={financialCovenantKey}
            chartConfig={createCovenantsChartConfig(configType)}
            key={financialCovenantKey}
            creditAgreementCovenant={creditAgreementFinancialCovenant}
            covenantData={allFinancialCovenantsData.successFinCovs}
            refetchCovenants={refetchSuccessFinancialCovenants}
            covenantType="financial"
          />
        );
      })}
    </>
  );
};
